import React, { useState, useEffect } from 'react';
import { getAllEmployeesWithTerminated, adminGetTimeEntries, adminCreateTimeEntry, adminUpdateTimeEntry, adminDeleteTimeEntry, exportTimePDF, getTimeBalance, getOpenTimeEntries, closeOpenTimeEntry, getTimeChangeRequests, approveTimeChangeRequest, rejectTimeChangeRequest, cleanupOldTimeChangeRequests } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Clock, Plus, Loader2, Trash2, Pencil, Users, Calendar, FileDown, TrendingUp, TrendingDown, RefreshCw, AlertTriangle, CheckCircle, XCircle, ClipboardList, Eraser } from 'lucide-react';

export const AdminTimeManagementPage = () => {
    const [employees, setEmployees] = useState([]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    const [entries, setEntries] = useState([]);
    const [openEntries, setOpenEntries] = useState([]);
    const [timeBalance, setTimeBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [closeEntryDialogOpen, setCloseEntryDialogOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [saving, setSaving] = useState(false);
    const [closeEndTime, setCloseEndTime] = useState('17:00');
    const [closeBreakMinutes, setCloseBreakMinutes] = useState(30);
    
    // Time Change Requests State
    const [pendingRequests, setPendingRequests] = useState([]);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [activeTab, setActiveTab] = useState('entries');
    
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    const [formData, setFormData] = useState({
        user_id: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '08:30',
        end_time: '14:30',
        break_minutes: 0,
        entry_type: 'WORK',
        notes: '',
        early_leave_reason: ''
    });

    const months = [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];

    useEffect(() => {
        loadEmployees();
        loadOpenEntries();
        loadPendingRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedEmployeeId) {
            loadEntries();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEmployeeId, selectedMonth, selectedYear]);

    const loadEmployees = async () => {
        try {
            // Use getAllEmployeesWithTerminated to include terminated employees for viewing historical time entries
            const res = await getAllEmployeesWithTerminated();
            setEmployees(res.data);
            if (res.data.length > 0) {
                setSelectedEmployeeId(res.data[0].id);
            }
        } catch (err) {
            toast.error('Fehler beim Laden der Mitarbeiter');
        } finally {
            setLoading(false);
        }
    };

    const loadOpenEntries = async () => {
        try {
            const res = await getOpenTimeEntries();
            setOpenEntries(res.data || []);
        } catch (error) {
            // Silent fail - entries optional
        }
    };

    const loadPendingRequests = async () => {
        try {
            const res = await getTimeChangeRequests({ status: 'PENDING' });
            setPendingRequests(res.data || []);
        } catch (error) {
            // Silent fail - requests optional
        }
    };

    const handleApproveRequest = async (requestId) => {
        setSaving(true);
        try {
            // Get the request to know which employee's entries to refresh
            const request = pendingRequests.find(r => r.id === requestId);
            await approveTimeChangeRequest(requestId);
            toast.success('Antrag genehmigt');
            
            // Refresh pending requests list
            await loadPendingRequests();
            
            // If the approved request was for the currently selected employee, refresh their entries
            if (request && request.user_id === selectedEmployeeId) {
                await loadEntries();
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Genehmigen');
        } finally {
            setSaving(false);
        }
    };

    const openRejectDialog = (request) => {
        setSelectedRequest(request);
        setRejectReason('');
        setRejectDialogOpen(true);
    };

    const handleRejectRequest = async () => {
        if (!selectedRequest) return;
        setSaving(true);
        try {
            await rejectTimeChangeRequest(selectedRequest.id, rejectReason || null);
            toast.success('Antrag abgelehnt');
            setRejectDialogOpen(false);
            setSelectedRequest(null);
            loadPendingRequests();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Ablehnen');
        } finally {
            setSaving(false);
        }
    };

    const handleCleanupOldRequests = async () => {
        if (!confirm('Alle bereits genehmigten und abgelehnten Anträge unwiderruflich löschen?')) return;
        setSaving(true);
        try {
            const res = await cleanupOldTimeChangeRequests();
            toast.success(res.data.message);
            loadPendingRequests();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Bereinigen');
        } finally {
            setSaving(false);
        }
    };

    const loadEntries = async () => {
        try {
            const res = await adminGetTimeEntries({
                user_id: selectedEmployeeId,
                month: selectedMonth,
                year: selectedYear
            });
            setEntries(res.data);
            
            // Load time balance for this employee
            try {
                const balanceRes = await getTimeBalance(selectedEmployeeId);
                setTimeBalance(balanceRes.data);
            } catch (e) {
                setTimeBalance(null);
            }
        } catch (err) {
            toast.error('Fehler beim Laden der Einträge');
        }
    };

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await loadEntries();
            await loadOpenEntries();
            await loadPendingRequests();
            toast.success('Aktualisiert');
        } catch (err) {
            toast.error('Fehler beim Aktualisieren');
        } finally {
            setRefreshing(false);
        }
    };

    const openCloseEntryDialog = (entry) => {
        setSelectedEntry(entry);
        setCloseEndTime('17:00');
        setCloseBreakMinutes(30);
        setCloseEntryDialogOpen(true);
    };

    const handleCloseOpenEntry = async () => {
        if (!selectedEntry) return;
        setSaving(true);
        try {
            await closeOpenTimeEntry(selectedEntry.id, closeEndTime, closeBreakMinutes);
            toast.success('Eintrag geschlossen');
            setCloseEntryDialogOpen(false);
            loadOpenEntries();
            loadEntries();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Schließen');
        } finally {
            setSaving(false);
        }
    };

    const openCreateDialog = () => {
        const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
        // Use employee's work times from master data if available
        const defaultStartTime = selectedEmployee?.work_start_time || '08:30';
        const defaultEndTime = selectedEmployee?.work_end_time || '14:30';
        
        setFormData({
            user_id: selectedEmployeeId,
            date: new Date().toISOString().split('T')[0],
            start_time: defaultStartTime,
            end_time: defaultEndTime,
            break_minutes: 0,
            entry_type: 'WORK',
            notes: '',
            early_leave_reason: ''
        });
        setSelectedEntry(null);
        setDialogOpen(true);
    };

    const openEditDialog = (entry) => {
        setFormData({
            user_id: entry.user_id,
            date: entry.date,
            start_time: entry.start_time,
            end_time: entry.end_time,
            break_minutes: entry.break_minutes || 0,
            entry_type: entry.entry_type,
            notes: entry.notes || '',
            early_leave_reason: entry.early_leave_reason || ''
        });
        setSelectedEntry(entry);
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (selectedEntry) {
                await adminUpdateTimeEntry(selectedEntry.id, {
                    start_time: formData.start_time,
                    end_time: formData.end_time,
                    break_minutes: formData.break_minutes,
                    notes: formData.notes || null,
                    early_leave_reason: formData.early_leave_reason || null
                });
                toast.success('Eintrag aktualisiert');
            } else {
                await adminCreateTimeEntry(formData);
                toast.success('Eintrag erstellt');
            }
            setDialogOpen(false);
            loadEntries();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    // Helper functions for entry type styling
    const getEntryTypeClass = (entryType) => {
        const classes = {
            'WORK': 'bg-primary/20 text-primary',
            'VACATION': 'bg-emerald-100 text-emerald-700',
            'HOLIDAY': 'bg-amber-100 text-amber-700',
            'PAID_LEAVE': 'bg-cyan-100 text-cyan-700',
            'UNEXCUSED_ABSENCE': 'bg-purple-100 text-purple-700',
        };
        return classes[entryType] || 'bg-red-100 text-red-700';
    };

    const getEntryTypeLabel = (entryType) => {
        const labels = {
            'WORK': 'Arbeit',
            'VACATION': 'Urlaub',
            'HOLIDAY': 'Feiertag',
            'PAID_LEAVE': 'Freigestellt',
            'UNEXCUSED_ABSENCE': 'Unentsch. gefehlt',
        };
        return labels[entryType] || 'Krank';
    };

    const confirmDelete = (entry) => {
        setSelectedEntry(entry);
        setDeleteDialogOpen(true);
    };

    const handleDownloadPDF = async () => {
        if (!selectedEmployeeId) {
            toast.error('Bitte wählen Sie einen Mitarbeiter aus');
            return;
        }
        
        try {
            const response = await exportTimePDF(selectedMonth, selectedYear, selectedEmployeeId);
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            const employee = employees.find(e => e.id === selectedEmployeeId);
            const fileName = `Zeiterfassung_${employee?.name?.replace(' ', '_') || 'Mitarbeiter'}_${months[selectedMonth - 1]}_${selectedYear}.pdf`;
            
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('PDF heruntergeladen');
        } catch {
            toast.error('Fehler beim Erstellen des PDFs');
        }
    };

    const handleDelete = async () => {
        try {
            await adminDeleteTimeEntry(selectedEntry.id);
            toast.success('Eintrag gelöscht');
            setDeleteDialogOpen(false);
            loadEntries();
        } catch (err) {
            toast.error('Fehler beim Löschen');
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
    };

    const calculateHours = (start, end, breakMin = 0) => {
        if (!start || !end) return '0.00';
        const [startH, startM] = start.split(':').map(Number);
        const [endH, endM] = end.split(':').map(Number);
        const totalMinutes = (endH * 60 + endM) - (startH * 60 + startM) - breakMin;
        return (totalMinutes / 60).toFixed(2);
    };

    const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
    
    // Fixed values
    const MONTHLY_TARGET = 130;
    const HOURS_PER_DAY = 6;
    
    // Check if employee has target (WEEKLY) or not (HOURLY)
    const hasTarget = selectedEmployee?.work_type !== 'HOURLY';
    
    // Calculate totals
    const workHours = entries.filter(e => e.entry_type === 'WORK').reduce((sum, e) => sum + (e.total_hours || 0), 0);
    const vacationDays = entries.filter(e => e.entry_type === 'VACATION').length;
    const sickDays = entries.filter(e => e.entry_type === 'SICK').length;
    const holidayDays = entries.filter(e => e.entry_type === 'HOLIDAY').length;
    const unexcusedAbsenceDays = entries.filter(e => e.entry_type === 'UNEXCUSED_ABSENCE').length;
    const paidLeaveDays = entries.filter(e => e.entry_type === 'PAID_LEAVE').length;
    
    // Calculate hours (based on employee's hours per day)
    const vacationHours = vacationDays * HOURS_PER_DAY;
    const sickHours = sickDays * HOURS_PER_DAY;
    const holidayHours = holidayDays * HOURS_PER_DAY;
    const paidLeaveHours = paidLeaveDays * HOURS_PER_DAY; // This counts as work time
    // Unentschuldigt gefehlt = 0 Stunden (nur Dokumentation, kein zusätzlicher Abzug)
    
    // Monats-Ist = Arbeit + Urlaub + Krank + Feiertag + Freigestellt
    // Unentschuldigt gefehlt zählt als 0 Stunden (nicht als Minus!)
    const monthlyActual = workHours + vacationHours + sickHours + holidayHours + paidLeaveHours;

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="admin-time-management-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                        Arbeitszeitverwaltung
                        <button 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Aktualisieren"
                        >
                            <RefreshCw size={20} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </h1>
                    <p className="text-muted-foreground">Arbeitszeiten für Mitarbeiter verwalten</p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        onClick={handleDownloadPDF}
                        disabled={!selectedEmployeeId}
                        data-testid="download-pdf-btn"
                    >
                        <FileDown className="h-4 w-4 mr-2" />
                        PDF Export
                    </Button>
                    <Button onClick={openCreateDialog} data-testid="create-entry-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Neuer Eintrag
                    </Button>
                </div>
            </div>

            {/* Tabs Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                    <TabsTrigger value="entries" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Zeiteinträge
                    </TabsTrigger>
                    <TabsTrigger value="requests" className="flex items-center gap-2" data-testid="pending-requests-tab">
                        <ClipboardList className="h-4 w-4" />
                        Anträge
                        {pendingRequests.length > 0 && (
                            <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                                {pendingRequests.length}
                            </Badge>
                        )}
                    </TabsTrigger>
                </TabsList>

                {/* Pending Requests Tab */}
                <TabsContent value="requests" className="space-y-4 mt-4">
                    {pendingRequests.length === 0 ? (
                        <Card>
                            <CardContent className="p-8 text-center">
                                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                                <p className="text-lg font-semibold text-muted-foreground">Keine offenen Anträge</p>
                                <p className="text-sm text-muted-foreground">Alle Zeitanträge wurden bearbeitet.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {pendingRequests.map((request) => (
                                <Card key={request.id} className="border-amber-200 bg-amber-50/50" data-testid={`request-card-${request.id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="font-semibold text-lg">{request.user_name}</span>
                                                    <Badge variant={request.request_type === 'NEW' ? 'default' : 'secondary'}>
                                                        {request.request_type === 'NEW' ? 'Neue Zeit' : 'Änderung'}
                                                    </Badge>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                                    <div>
                                                        <span className="text-muted-foreground">Datum:</span>
                                                        <p className="font-medium">{new Date(request.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Beginn:</span>
                                                        <p className="font-medium">{request.start_time || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Ende:</span>
                                                        <p className="font-medium">{request.end_time || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Pause:</span>
                                                        <p className="font-medium">{request.break_minutes || 0} Min</p>
                                                    </div>
                                                </div>
                                                {request.reason && request.reason !== 'Tägliche Arbeitszeit' && (
                                                    <div className="mt-2 p-2 bg-white rounded border">
                                                        <span className="text-xs text-muted-foreground">Bemerkung:</span>
                                                        <p className="text-sm">{request.reason}</p>
                                                    </div>
                                                )}
                                                <p className="text-xs text-muted-foreground mt-2">
                                                    Eingereicht am {new Date(request.created_at).toLocaleDateString('de-DE')} um {new Date(request.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                            <div className="flex gap-2 md:flex-col">
                                                <Button
                                                    onClick={() => handleApproveRequest(request.id)}
                                                    disabled={saving}
                                                    className="bg-green-600 hover:bg-green-700 flex-1"
                                                    data-testid={`approve-${request.id}`}
                                                >
                                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                                                    Genehmigen
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    onClick={() => openRejectDialog(request)}
                                                    disabled={saving}
                                                    className="flex-1"
                                                    data-testid={`reject-${request.id}`}
                                                >
                                                    <XCircle className="h-4 w-4 mr-1" />
                                                    Ablehnen
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                    
                    {/* Cleanup Button */}
                    <div className="flex justify-end pt-4 border-t">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCleanupOldRequests}
                            disabled={saving}
                            className="text-muted-foreground"
                            data-testid="cleanup-requests-btn"
                        >
                            <Eraser className="h-4 w-4 mr-2" />
                            Alte Anträge bereinigen
                        </Button>
                    </div>
                </TabsContent>

                {/* Time Entries Tab */}
                <TabsContent value="entries" className="space-y-6 mt-4">
                    {/* Open Entries Warning */}
                    {openEntries.length > 0 && (
                        <Card className="border-amber-300 bg-amber-50" data-testid="open-entries-warning">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                                    <AlertTriangle className="h-5 w-5" />
                                    Offene Einträge - Mitarbeiter nicht ausgestempelt
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-amber-600 mb-3">
                                    Diese Mitarbeiter haben vergessen auszustempeln. Um 21:00 werden sie automatisch ausgestempelt.
                                </p>
                                <div className="space-y-2">
                                    {openEntries.map(entry => (
                                        <div 
                                            key={entry.id}
                                            className="p-3 bg-white border border-amber-200 rounded-lg flex items-center justify-between"
                                        >
                                            <div>
                                                <p className="font-semibold">{entry.user_name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {entry.date} • Eingestempelt um {entry.start_time}
                                                </p>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openCloseEntryDialog(entry)}
                                                data-testid={`close-entry-${entry.id}`}
                                            >
                                                Manuell schließen
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex-1 min-w-[200px]">
                            <Label className="mb-2 block">Mitarbeiter</Label>
                            <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                                <SelectTrigger data-testid="employee-select">
                                    <SelectValue placeholder="Mitarbeiter wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {employees.map(emp => {
                                        const isTerminated = emp.termination_date && new Date(emp.termination_date) <= new Date();
                                        return (
                                            <SelectItem key={emp.id} value={emp.id}>
                                                {emp.name}
                                                {isTerminated && <span className="ml-2 text-xs text-red-600">(Ausgeschieden)</span>}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-2 block">Monat</Label>
                            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                                <SelectTrigger className="w-[140px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {months.map((month, index) => (
                                        <SelectItem key={`month-${index + 1}`} value={(index + 1).toString()}>
                                            {month}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-2 block">Jahr</Label>
                            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                                <SelectTrigger className="w-[100px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[2024, 2025, 2026, 2027].map(year => (
                                        <SelectItem key={year} value={year.toString()}>
                                            {year}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Summary */}
            {selectedEmployee && (
                <div className={`grid grid-cols-2 ${hasTarget ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
                    {hasTarget && (
                        <Card>
                            <CardContent className="p-4 text-center">
                                <Clock className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                                <p className="text-2xl font-bold text-muted-foreground">{MONTHLY_TARGET}</p>
                                <p className="text-xs text-muted-foreground">Monats-Soll</p>
                            </CardContent>
                        </Card>
                    )}
                    <Card className="bg-primary/5 border-primary">
                        <CardContent className="p-4 text-center">
                            <Clock className="h-6 w-6 mx-auto mb-2 text-primary" />
                            <p className="text-2xl font-bold text-primary">{monthlyActual.toFixed(1)}</p>
                            <p className="text-xs text-muted-foreground font-medium">{hasTarget ? 'Monats-Ist' : 'Stunden'}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <Calendar className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                            <p className="text-2xl font-bold text-blue-600">{vacationDays}</p>
                            <p className="text-xs text-muted-foreground">Urlaubstage</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <Calendar className="h-6 w-6 mx-auto mb-2 text-red-600" />
                            <p className="text-2xl font-bold text-red-600">{sickDays}</p>
                            <p className="text-xs text-muted-foreground">Krankheitstage</p>
                        </CardContent>
                    </Card>
                    {paidLeaveDays > 0 && (
                        <Card className="border-cyan-300 bg-cyan-50">
                            <CardContent className="p-4 text-center">
                                <Calendar className="h-6 w-6 mx-auto mb-2 text-cyan-600" />
                                <p className="text-2xl font-bold text-cyan-600">{paidLeaveDays}</p>
                                <p className="text-xs text-muted-foreground">Freigestellt</p>
                            </CardContent>
                        </Card>
                    )}
                    {unexcusedAbsenceDays > 0 && (
                        <Card className="border-purple-300 bg-purple-50">
                            <CardContent className="p-4 text-center">
                                <Calendar className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                                <p className="text-2xl font-bold text-purple-600">{unexcusedAbsenceDays}</p>
                                <p className="text-xs text-muted-foreground">Unentsch. gefehlt</p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Time Balance Card - Only for WEEKLY employees */}
            {timeBalance && timeBalance.balance_hours !== null && (
                <Card className={`border-2 ${timeBalance.balance_hours >= 0 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`} data-testid="time-balance-card">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Zeitkonto {timeBalance.year}</p>
                                <p className={`text-3xl font-bold ${timeBalance.balance_hours >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {timeBalance.balance_formatted}
                                </p>
                            </div>
                            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${timeBalance.balance_hours >= 0 ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                                {timeBalance.balance_hours >= 0 ? <TrendingUp className="h-6 w-6" /> : <TrendingDown className="h-6 w-6" />}
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            Soll: {timeBalance.total_expected}h | Ist: {timeBalance.total_actual}h
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Stand: {new Date(timeBalance.as_of_date).toLocaleDateString('de-DE')}
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Entries Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">
                        Einträge {months[selectedMonth - 1]} {selectedYear}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {entries.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">
                            Keine Einträge für diesen Zeitraum
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left p-2">Datum</th>
                                        <th className="text-left p-2">Typ</th>
                                        <th className="text-left p-2">Beginn</th>
                                        <th className="text-left p-2">Ende</th>
                                        <th className="text-left p-2">Pause</th>
                                        <th className="text-left p-2">Stunden</th>
                                        <th className="text-left p-2">Bemerkung</th>
                                        <th className="text-right p-2">Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map(entry => (
                                        <tr key={entry.id} className="border-b hover:bg-muted/50">
                                            <td className="p-2">{formatDate(entry.date)}</td>
                                            <td className="p-2">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getEntryTypeClass(entry.entry_type)}`}>
                                                    {getEntryTypeLabel(entry.entry_type)}
                                                </span>
                                            </td>
                                            <td className="p-2">{entry.start_time}</td>
                                            <td className="p-2">{entry.end_time}</td>
                                            <td className="p-2">{entry.break_minutes || 0} Min</td>
                                            <td className="p-2 font-semibold">{entry.total_hours?.toFixed(2)}</td>
                                            <td className="p-2 text-muted-foreground text-sm max-w-[150px] truncate">
                                                {entry.early_leave_reason ? (
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                        entry.early_leave_reason === 'BAD_WEATHER' 
                                                            ? 'bg-amber-100 text-amber-700' 
                                                            : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                        {entry.early_leave_reason === 'BAD_WEATHER' ? 'Schlechtwetter' : 'Geplant'}
                                                    </span>
                                                ) : entry.notes || '-'}
                                            </td>
                                            <td className="p-2 text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEditDialog(entry)}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => confirmDelete(entry)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
                </TabsContent>
            </Tabs>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="time-entry-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedEntry ? 'Eintrag bearbeiten' : 'Neuer Zeiteintrag'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {!selectedEntry && (
                            <div className="space-y-2">
                                <Label>Mitarbeiter</Label>
                                <Select 
                                    value={formData.user_id} 
                                    onValueChange={(v) => setFormData({...formData, user_id: v})}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Mitarbeiter wählen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {employees.map(emp => {
                                            const isTerminated = emp.termination_date && new Date(emp.termination_date) <= new Date();
                                            return (
                                                <SelectItem key={emp.id} value={emp.id}>
                                                    {emp.name}
                                                    {isTerminated && <span className="ml-2 text-xs text-red-600">(Ausgeschieden)</span>}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Datum</Label>
                                <Input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                    disabled={!!selectedEntry}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Typ</Label>
                                <Select 
                                    value={formData.entry_type} 
                                    onValueChange={(v) => setFormData({...formData, entry_type: v})}
                                    disabled={!!selectedEntry}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="WORK">Arbeit</SelectItem>
                                        <SelectItem value="VACATION">Urlaub</SelectItem>
                                        <SelectItem value="SICK">Krank</SelectItem>
                                        <SelectItem value="HOLIDAY">Feiertag</SelectItem>
                                        <SelectItem value="PAID_LEAVE">Freigestellt</SelectItem>
                                        <SelectItem value="UNEXCUSED_ABSENCE">Unentschuldigt gefehlt</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Beginn</Label>
                                <Input
                                    type="time"
                                    value={formData.start_time}
                                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ende</Label>
                                <Input
                                    type="time"
                                    value={formData.end_time}
                                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Pause (Min)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={formData.break_minutes}
                                    onChange={(e) => setFormData({...formData, break_minutes: parseInt(e.target.value) || 0})}
                                />
                            </div>
                        </div>
                        <div className="p-3 bg-muted rounded-lg text-center">
                            <p className="text-sm text-muted-foreground">Berechnete Arbeitszeit</p>
                            <p className="text-2xl font-bold">
                                {calculateHours(formData.start_time, formData.end_time, formData.break_minutes)} Stunden
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Notizen (optional)</Label>
                            <Textarea
                                placeholder="Optionale Notizen..."
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Grund für frühen Feierabend (optional)</Label>
                            <Select 
                                value={formData.early_leave_reason || "NONE"} 
                                onValueChange={(v) => setFormData({...formData, early_leave_reason: v === "NONE" ? "" : v})}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Kein Grund" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="NONE">Kein Grund</SelectItem>
                                    <SelectItem value="PLANNED">Geplanter Feierabend</SelectItem>
                                    <SelectItem value="BAD_WEATHER">Schlechtwetter</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                {selectedEntry ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eintrag löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie diesen Zeiteintrag wirklich löschen?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Close Open Entry Dialog */}
            <Dialog open={closeEntryDialogOpen} onOpenChange={setCloseEntryDialogOpen}>
                <DialogContent data-testid="close-entry-dialog">
                    <DialogHeader>
                        <DialogTitle>Offenen Eintrag schließen</DialogTitle>
                    </DialogHeader>
                    {selectedEntry && (
                        <div className="space-y-4">
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="font-semibold">{selectedEntry.user_name}</p>
                                <p className="text-sm text-muted-foreground">
                                    Datum: {selectedEntry.date} • Eingestempelt: {selectedEntry.start_time}
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Ausstempelzeit</Label>
                                    <Input
                                        type="time"
                                        value={closeEndTime}
                                        onChange={(e) => setCloseEndTime(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Pause (Minuten)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="120"
                                        value={closeBreakMinutes}
                                        onChange={(e) => setCloseBreakMinutes(parseInt(e.target.value) || 0)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCloseEntryDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleCloseOpenEntry} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Eintrag schließen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject Request Dialog */}
            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <DialogContent data-testid="reject-request-dialog">
                    <DialogHeader>
                        <DialogTitle>Antrag ablehnen</DialogTitle>
                    </DialogHeader>
                    {selectedRequest && (
                        <div className="space-y-4">
                            <div className="p-3 bg-muted rounded-lg">
                                <p className="font-semibold">{selectedRequest.user_name}</p>
                                <p className="text-sm text-muted-foreground">
                                    {new Date(selectedRequest.date).toLocaleDateString('de-DE')} • {selectedRequest.start_time} - {selectedRequest.end_time}
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label>Ablehnungsgrund (optional)</Label>
                                <Textarea
                                    placeholder="z.B. Zeiten stimmen nicht mit dem Arbeitsplan überein..."
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button variant="destructive" onClick={handleRejectRequest} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Ablehnen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminTimeManagementPage;
